import type { QueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { mapLimited, mapLimitedSettled } from "./done/pagedApprovedSnapshot"
import { loadFrameworkState, type FrameworkState } from "./loadFrameworkState"
import type { StatementCard, StudioV2State } from "./useStudioV2State"

/** Display reads accept a slice this old (the review's own slice freshness); every
 *  save marks the slices it touches stale, so an edited regulation is read again. */
const DISPLAY_STALE_MS = 30_000
const LOAD_CONCURRENCY = 2

type WriteSeq = number | null | undefined

interface KeptCopy {
  cards: StatementCard[]
  /** The open items' stored version this copy was asked under. */
  writeSeq: WriteSeq
}

export interface LoadOtherCardsOptions {
  /** Only the regulations with open work (the open-points map names nothing else);
   *  otherwise every regulation that is not open (the request dialog quotes each). */
  openOnly?: boolean
}

export interface PagedGestures {
  /** Bulk conform: one regulation, read fresh (it writes on this basis). */
  loadState: (framework: string) => Promise<FrameworkState>
  /** "Link all": every regulation's cards, read fresh (it writes on this basis). */
  loadEveryCard: () => Promise<StatementCard[]>
  /** The request dialog and the open-points map: other regulations' cards, kept in
   *  `otherCards` so the footer reads them like the open one's. Resolves to the
   *  regulations that could NOT be loaded (empty when every load worked) — the
   *  caller says so rather than quoting a partial declaration silently. */
  loadOtherCards: (options?: LoadOtherCardsOptions) => Promise<string[]>
  /** Cards of regulations that are not open, loaded under the open items' current
   *  stored version (stale copies are kept but not shown). */
  otherCards: ReadonlyMap<string, StatementCard[]>
  /** Drops every kept copy (after a gesture that wrote to other regulations). */
  forgetOtherCards: () => void
}

/**
 * STEP C3-b3: WHAT THE PAGED REVIEW'S CROSS-REGULATION GESTURES LOAD.
 *
 * The page holds the open regulation's cards only. Every gesture or text that needs
 * another regulation loads it here, derived exactly as flag off derives it
 * (`loadFrameworkState`), at most two downloads at a time. Undefined outside the paged
 * review: the full reads already hold every regulation.
 *
 * Besides the on-demand loads, the regulation holding the FIRST open point is loaded
 * on its own when it is not the open one, because the footer names that point (gate
 * line, Approve tooltip) with its statement's own reason text. Only once the open
 * regulation's slice has landed, so it never competes with what the reviewer is
 * looking at.
 *
 * Every kept copy remembers the stored version of the open items it was loaded under.
 * When the open items move to a newer version (any save, here or in another session)
 * the copy goes STALE: it is kept but no longer shown, so the footer falls back to the
 * summary's verdict wording for that regulation, and it is read again only when its
 * text is actually needed (the open-points map or the request dialog opens) — never as
 * a side effect of a save, which with the declaration tables on bumps the version every
 * time. A load still on its way when the version moves lands stale for the same
 * reason. A copy is dropped outright the moment its regulation opens (it is edited
 * from its own slice from then on).
 */
export function usePagedGestures(
  queryClient: QueryClient | undefined,
  state: StudioV2State,
): PagedGestures | undefined {
  const enabled = state.paged === true && !!queryClient
  const { declId, frameworks, activeFramework, pendingIssues } = state
  const draftedStatements = state.draftedStatements
  const inquiryStateOf = state.cascadeInquiryStateOf
  const notOlderThan = state.summaryWriteSeq
  const [kept, setKept] = useState<Map<string, KeptCopy>>(() => new Map())
  // Shown copies only: those loaded under the open items' current stored version.
  const otherCards = useMemo(() => {
    const fresh = new Map<string, StatementCard[]>()
    for (const [fw, copy] of kept)
      if (copy.writeSeq === notOlderThan) fresh.set(fw, copy.cards)
    return fresh
  }, [kept, notOlderThan])
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(
    (framework: string, staleTime?: number, atLeast: WriteSeq = notOlderThan) =>
      loadFrameworkState(queryClient!, declId, framework, {
        staleTime,
        pendingIssues,
        draftedStatements,
        inquiryStateOf,
        notOlderThan: atLeast,
      }),
    [
      queryClient,
      declId,
      pendingIssues,
      draftedStatements,
      inquiryStateOf,
      notOlderThan,
    ],
  )
  // Each load is tagged with the version it was asked under, never the version current
  // when it lands: a copy the open items have since moved past lands stale.
  const keep = useCallback(
    (states: readonly FrameworkState[], writeSeq: WriteSeq) => {
      if (!mounted.current || states.length === 0) return
      setKept((prev) => {
        const next = new Map(prev)
        for (const s of states)
          next.set(s.framework, { cards: s.cards, writeSeq })
        return next
      })
    },
    [],
  )
  const forgetOtherCards = useCallback(() => {
    if (mounted.current) setKept(new Map())
  }, [])

  const seqRef = useRef(notOlderThan)
  useEffect(() => {
    seqRef.current = notOlderThan
  }, [notOlderThan])

  const loadState = useCallback((fw: string) => load(fw), [load])
  const loadEveryCard = useCallback(async () => {
    const states = await mapLimited(frameworks, LOAD_CONCURRENCY, (fw) =>
      load(fw),
    )
    keep(
      states.filter((s) => s.framework !== activeFramework),
      notOlderThan,
    )
    return states.flatMap((s) => s.cards)
  }, [frameworks, activeFramework, load, keep, notOlderThan])
  const openByFramework = state.openByFramework
  // What `loadOtherCards` checks once its loads land: the regulation open NOW and the
  // open items' version NOW (the reviewer may switch, or save, while it loads).
  const activeRef = useRef(activeFramework)
  useEffect(() => {
    activeRef.current = activeFramework
  }, [activeFramework])
  const loadOtherCards = useCallback(
    async (options: LoadOtherCardsOptions = {}) => {
      const wanted = () =>
        frameworks.filter(
          (fw) =>
            fw !== activeRef.current &&
            (!options.openOnly || (openByFramework.get(fw)?.length ?? 0) > 0),
        )
      // The version each regulation was loaded under in THIS call, and the ones whose
      // last load failed. Never rejects: anything that goes wrong is reported as the
      // regulations that could not be loaded, which are never the open one.
      const loadedUnder = new Map<string, WriteSeq>()
      const failed = new Set<string>()
      try {
        // A second pass covers what changed while the first ran: a regulation the
        // reviewer just left (it was open, so not loaded, and is not on screen any
        // more) and copies a save made stale (read again under the new version).
        for (let pass = 0; pass < 2; pass++) {
          const seq = seqRef.current
          const todo = wanted().filter(
            (fw) => !loadedUnder.has(fw) || loadedUnder.get(fw) !== seq,
          )
          if (todo.length === 0) break
          const settled = await mapLimitedSettled(
            todo,
            LOAD_CONCURRENCY,
            (fw) => load(fw, DISPLAY_STALE_MS, seq),
          )
          keep(
            settled.flatMap((r) => (r.status === "fulfilled" ? [r.value] : [])),
            seq,
          )
          todo.forEach((fw, i) => {
            if (settled[i].status === "fulfilled") {
              loadedUnder.set(fw, seq)
              failed.delete(fw)
            } else failed.add(fw)
          })
        }
      } catch {
        return wanted()
      }
      // Missing: failed, never loaded, or loaded under a version the open items have
      // since moved past (such a copy is kept stale and not shown, so the caller must
      // not count on its text).
      const now = seqRef.current
      return wanted().filter(
        (fw) => failed.has(fw) || loadedUnder.get(fw) !== now,
      )
    },
    [frameworks, openByFramework, load, keep],
  )

  // Adjusted during render, React's pattern for state that follows a prop: a
  // regulation the reviewer opens is edited from its own slice from then on, so a
  // copy loaded while it was not open is dropped the moment it opens. (A newer stored
  // version only makes copies stale; see `otherCards`.)
  const [openedFramework, setOpenedFramework] = useState(activeFramework)
  if (openedFramework !== activeFramework) {
    setOpenedFramework(activeFramework)
    if (activeFramework && kept.has(activeFramework)) {
      const next = new Map(kept)
      next.delete(activeFramework)
      setKept(next)
    }
  }

  // The regulation of the footer's first open point: the first one in navigator
  // order with open work. Loaded on its own only when that is not the open one, and
  // only after the open regulation's slice has landed.
  const firstOpen = enabled
    ? frameworks.find((fw) => (openByFramework.get(fw)?.length ?? 0) > 0)
    : undefined
  // Any kept copy, stale or not, counts as loaded here: a save must never trigger a
  // download of its own, only a reader that needs the text (`loadOtherCards`).
  const preload =
    firstOpen && firstOpen !== activeFramework && !kept.has(firstOpen)
      ? firstOpen
      : undefined
  const activeSettled = state.maskLoading === false
  // One preload per regulation at a time: `load` changes with every new version, which
  // re-runs this effect, and a preload still on its way must not be asked again.
  const preloading = useRef(new Set<string>())
  useEffect(() => {
    if (!enabled || !preload || !activeSettled) return
    if (preloading.current.has(preload)) return
    preloading.current.add(preload)
    const askedUnder = seqRef.current
    load(preload, DISPLAY_STALE_MS)
      .then(
        (s) => keep([s], askedUnder),
        () => {},
      )
      .finally(() => preloading.current.delete(preload))
  }, [enabled, preload, activeSettled, load, keep])

  return useMemo(
    () =>
      enabled
        ? {
            loadState,
            loadEveryCard,
            loadOtherCards,
            otherCards,
            forgetOtherCards,
          }
        : undefined,
    [
      enabled,
      loadState,
      loadEveryCard,
      loadOtherCards,
      otherCards,
      forgetOtherCards,
    ],
  )
}
